import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { OrderCancelProvider } from "./OrderCancelContext";

const OrdersContext = createContext();

export const useOrders = () => useContext(OrdersContext);

export const OrdersProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add ref to track processed orders and prevent duplicates
  const processedOrders = useRef(new Set());
  const orderPlacementInProgress = useRef(false);

  // Helper function to consolidate timeline steps
  const consolidateTimeline = (statusHistories, order) => {
    if (!statusHistories || !Array.isArray(statusHistories) || statusHistories.length === 0) {
      // Fallback to old audit fields for backward compatibility
      const fallbackTimeline = [
        order.placed_audit_time && {
          status: "Placed",
          description: "Order Placed",
          date: order.placed_audit_time.split(" ")[0],
          time: order.placed_audit_time.split(" ")[1],
        },
        (order.processing_audit_time || order.packaging_audit_time) && {
          status: "Processing",
          description: "Awaiting Process",
          date: (order.processing_audit_time || order.packaging_audit_time).split(" ")[0],
          time: (order.processing_audit_time || order.packaging_audit_time).split(" ")[1],
        },
        order.shipped_audit_time && {
          status: "Shipped",
          description: "Shipped",
          date: order.shipped_audit_time.split(" ")[0],
          time: order.shipped_audit_time.split(" ")[1],
        },
        order.delivered_audit_time && {
          status: "Delivered",
          description: "Delivered",
          date: order.delivered_audit_time.split(" ")[0],
          time: order.delivered_audit_time.split(" ")[1],
        },
        order.returned_audit_time && {
          status: "return_requested",
          description: "Return Requested",
          date: order.returned_audit_time.split(" ")[0],
          time: order.returned_audit_time.split(" ")[1],
        },
        order.cancel_requested_audit_time && {
          status: "cancel_requested",
          description: "Cancel Requested",
          date: order.cancel_requested_audit_time.split(" ")[0],
          time: order.cancel_requested_audit_time.split(" ")[1],
        },
        order.cancelled_audit_time && {
          status: "Cancelled",
          description: "Cancelled",
          date: order.cancelled_audit_time.split(" ")[0],
          time: order.cancelled_audit_time.split(" ")[1],
        },
        order.rejected_audit_time && {
          status: "Rejected",
          description: "Rejected",
          date: order.rejected_audit_time.split(" ")[0],
          time: order.rejected_audit_time.split(" ")[1],
        },
        order.refund_audit_time && {
          status: "Refunded",
          description: "Refunded",
          date: order.refund_audit_time.split(" ")[0],
          time: order.refund_audit_time.split(" ")[1],
        },
      ].filter(Boolean);

      return fallbackTimeline;
    }

    // Process status histories and consolidate intermediate steps
    const consolidatedTimeline = [];
    let hasIntermediateStep = false;
    let intermediateStepData = null;

    // Define intermediate processing statuses that should be consolidated
    const intermediateStatuses = [
      'processing',
      'printing_started',
      'production_completed',
      'quality_check',
      'packaging'
    ];

    // Sort status histories by date/time to ensure proper order
    const sortedHistories = [...statusHistories].sort((a, b) => {
      const dateA = new Date(a.create_audit_time || '');
      const dateB = new Date(b.create_audit_time || '');
      return dateA - dateB;
    });

    // Filter out return_received status entries and track the last valid status
    const filteredHistories = [];
    let lastValidStatus = null;
    let returnReceivedFound = false;

    for (let i = 0; i < sortedHistories.length; i++) {
      const history = sortedHistories[i];
      const status = history.status?.toLowerCase();

      // Skip return_received status
      if (status === 'return_received') {
        returnReceivedFound = true;
        // If this is the last status in the array, we'll use the previous valid status
        if (i === sortedHistories.length - 1 && lastValidStatus) {
          // Don't add return_received, the previous status will be the final one
        }
        continue;
      }

      filteredHistories.push(history);
      lastValidStatus = history;
    }

    // If return_received was the final status and we have a previous valid status,
    // we'll use that as the effective final status
    let effectiveFinalStatus = null;
    if (returnReceivedFound && filteredHistories.length > 0) {
      effectiveFinalStatus = filteredHistories[filteredHistories.length - 1];
    }

    for (const history of filteredHistories) {
      const auditTime = history.create_audit_time || '';
      const [date, time] = auditTime.split(' ');
      const status = history.status?.toLowerCase();

      if (!date || !status) continue;

      // Handle different status types
      if (status === 'placed') {
        consolidatedTimeline.push({
          status: 'Placed',
          description: 'Order Placed',
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      } else if (intermediateStatuses.includes(status)) {
        // Consolidate all intermediate steps into one "Awaiting Process" step
        if (!hasIntermediateStep) {
          intermediateStepData = {
            status: 'Processing',
            description: 'Awaiting Process',
            date,
            time: time || '',
            content: history.content || null,
            shipping_details: history.shipping_details || null
          };
          hasIntermediateStep = true;
        }
        // Update the time to the latest intermediate step time
        else if (intermediateStepData) {
          intermediateStepData.date = date;
          intermediateStepData.time = time || '';
          // Keep the latest content and shipping details
          if (history.content) {
            intermediateStepData.content = history.content;
          }
          if (history.shipping_details) {
            intermediateStepData.shipping_details = history.shipping_details;
          }
        }
      } else if (status === 'shipped') {
        // Add the consolidated intermediate step before shipped (if it exists)
        if (hasIntermediateStep && intermediateStepData) {
          consolidatedTimeline.push(intermediateStepData);
          hasIntermediateStep = false;
          intermediateStepData = null;
        }

        consolidatedTimeline.push({
          status: 'Shipped',
          description: 'Shipped',
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      } else if (status === 'delivered') {
        // Add the consolidated intermediate step before delivered (if it exists and not already added)
        if (hasIntermediateStep && intermediateStepData) {
          consolidatedTimeline.push(intermediateStepData);
          hasIntermediateStep = false;
          intermediateStepData = null;
        }

        consolidatedTimeline.push({
          status: 'Delivered',
          description: 'Delivered',
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      } else {
        // Add the consolidated intermediate step before other statuses (if it exists and not already added)
        if (hasIntermediateStep && intermediateStepData) {
          consolidatedTimeline.push(intermediateStepData);
          hasIntermediateStep = false;
          intermediateStepData = null;
        }

        // Handle other statuses (returns, cancellations, etc.)
        const getStatusInfo = (status) => {
          switch (status) {
            case 'returned':
            case 'return_requested':
              return { status: 'return_requested', description: 'Return Requested' };
            case 'return_approved':
              return { status: 'return_approved', description: 'Return Approved' };
            case 'return_rejected':
              return { status: 'return_rejected', description: 'Return Rejected' };
            case 'cancel_requested':
            case 'cancellation_requested':
              return { status: 'cancel_requested', description: 'Cancel Requested' };
            case 'cancelled':
              return { status: 'Cancelled', description: 'Cancelled' };
            case 'rejected':
              return { status: 'Rejected', description: 'Rejected' };
            case 'refund_processing':
              return { status: 'refund_processing', description: 'Refund Processing' };
            case 'refund':
            case 'refunded':
              return { status: 'Refunded', description: 'Refunded' };
            case 'inspection_pending':
              return { status: 'inspection_pending', description: 'Inspection Pending' };
            case 'partial_refund':
              return { status: 'partial_refund', description: 'Partial Refund' };
            case 'replacement_processing':
              return { status: 'replacement_processing', description: 'Replacement Processing' };
            case 'returned_no_refund':
              return { status: 'returned_no_refund', description: 'Returned (No Refund)' };
            default:
              return { status: status || 'Unknown', description: status || 'Unknown Status' };
          }
        };

        const statusInfo = getStatusInfo(status);
        consolidatedTimeline.push({
          ...statusInfo,
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      }
    }

    // If we have an intermediate step that wasn't added (order is still in processing), add it
    if (hasIntermediateStep && intermediateStepData) {
      consolidatedTimeline.push(intermediateStepData);
    }

    return consolidatedTimeline;
  };

  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (!token || !user) {
      console.log("No authentication found, skipping order fetch");
      setError("Please login to view your orders.");
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userData = JSON.parse(user);
      const userId = userData?.id;

      console.log("Fetching orders for user:", userId);

      if (!userId) {
        throw new Error("User ID not found. Please login again.");
      }

      const res = await axios.get(`${BASE_URL}/orders/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 10000,
      });

      console.log("Order details API Response:", res.data);

      if (res.data.status !== "200") {
        throw new Error("Failed to fetch orders. Invalid response from server.");
      }

      // Map API response to your order format with proper null checks
      const formattedOrders = res.data.data
        .map((order) => {
          try {
            // Process all items, including those with null products
            const allItems = order.items || [];
            const validItems = [];
            const invalidItems = [];

            allItems.forEach((item) => {
              if (item.product !== null && item.product !== undefined) {
                try {
                  // Parse product images safely
                  let productImages = [];
                  try {
                    productImages = JSON.parse(
                      item.product.product_images || "[]",
                    );
                  } catch (parseError) {
                    console.warn(
                      "Failed to parse product images for item:",
                      item.id,
                      parseError,
                    );
                    productImages = [];
                  }

                  // Get the first image or use a default
                  const firstImage =
                    productImages.length > 0
                      ? productImages[0]
                      : "default-product.jpg";
                  const imageUrl = `${CDN_URL.replace(/\/$/, "")}/${firstImage}`;

                  validItems.push({
                    id: item.product.id,
                    orderItemId: item.id,
                    name: item.product.product_name || "Unknown Product",
                    category: item.product.category_name || "Unknown Category",
                    price: parseFloat(item.price) || 0,
                    quantity: parseInt(item.quantity) || 1,
                    image: imageUrl,
                    customization: {},
                  });
                } catch (itemError) {
                  console.error(
                    "Error formatting item:",
                    item.id,
                    itemError,
                  );
                  invalidItems.push(item);
                }
              } else {
                // Handle items with null products (unavailable items)
                invalidItems.push({
                  id: item.product_id || item.id,
                  orderItemId: item.id,
                  name: "Product No Longer Available",
                  category: "Unknown Category",
                  price: parseFloat(item.price) || 0,
                  quantity: parseInt(item.quantity) || 1,
                  image: "/default-product.jpg",
                  customization: {},
                  isUnavailable: true,
                });
              }
            });

            // Include orders even if they have some invalid items
            const displayItems =
              validItems.length > 0 ? validItems : invalidItems;

            if (displayItems.length === 0) {
              console.warn("Order has no items to display:", order.id);
              return null;
            }

            // Use the new consolidated timeline function
            const timeline = consolidateTimeline(order.status_histories, order);

            // Determine the effective order status
            // If return_received was filtered out and was the final status, use the previous status
            let effectiveOrderStatus = order.order_status || "Unknown";

            if (order.status_histories && order.status_histories.length > 0) {
              // Check if the last status in the original history was return_received
              const lastOriginalStatus = order.status_histories[order.status_histories.length - 1];
              if (lastOriginalStatus.status?.toLowerCase() === 'return_received') {
                // Find the previous valid status
                for (let i = order.status_histories.length - 2; i >= 0; i--) {
                  const prevStatus = order.status_histories[i];
                  if (prevStatus.status?.toLowerCase() !== 'return_received') {
                    effectiveOrderStatus = prevStatus.status;
                    break;
                  }
                }
              }
            }

            console.log("Original API status:", order.order_status, "-> Effective status:", effectiveOrderStatus);

            // Extract promo code information
            const promoCode = order.promo_code || null;
            const promoDiscount = order.promo_value ? parseFloat(order.promo_value) : 0;

            // Get order date from status_histories or fallback to audit fields
            let orderDate = new Date().toISOString().split("T")[0];
            if (order.status_histories && order.status_histories.length > 0) {
              const placedHistory = order.status_histories.find(h => h.status?.toLowerCase() === 'placed');
              if (placedHistory && placedHistory.create_audit_time) {
                orderDate = placedHistory.create_audit_time.split(" ")[0];
              }
            } else if (order.placed_audit_time) {
              orderDate = order.placed_audit_time.split(" ")[0];
            }

            // Get delivery date from status_histories or fallback to audit fields
            let deliveryDate = null;
            if (order.status_histories && order.status_histories.length > 0) {
              const deliveredHistory = order.status_histories.find(h => h.status?.toLowerCase() === 'delivered');
              if (deliveredHistory && deliveredHistory.create_audit_time) {
                deliveryDate = deliveredHistory.create_audit_time.split(" ")[0];
              }
            } else if (order.delivered_audit_time) {
              deliveryDate = order.delivered_audit_time.split(" ")[0];
            }

            return {
              id: order.id,
              orderNumber: order.order_id || `ORDER-${order.id}`,
              date: orderDate,
              status: effectiveOrderStatus,
              total: parseFloat(order.total) || 0,
              subtotal: parseFloat(order.subtotal) || 0,
              shippingCharges: parseFloat(order.shipping_charges) || 0,
              tax: parseFloat(order.tax) || 0,
              promoCode: promoCode,
              promoDiscount: promoDiscount,
              itemCount: displayItems.length,
              estimatedDelivery: deliveryDate,
              trackingNumber: order.transaction_id || null,
              items: displayItems,
              billingAddress: order.billing_address ? {
                firstName: order.billing_address.first_name || "",
                lastName: order.billing_address.last_name || "",
                email: order.billing_address.email || "",
                phone: order.billing_address.phone || "",
                address: order.billing_address.address || "",
                city: order.billing_address.city || "",
                state: order.billing_address.state || "",
                pincode: order.billing_address.pincode || "",
              } : null,
              shippingAddress: {
                name: order.address?.address || "No address",
                street: order.address?.street || "",
                city: order.address?.city || "",
                state: order.address?.state || "",
                zipCode: order.address?.zipcode || "",
              },
              timeline: timeline,
              paymentMethod: order.payment_method || "Unknown",
              paymentStatus: order.payment_status || "Unknown",
              hasUnavailableItems: invalidItems.length > 0,
              reviewStatus: order.review_status || false,
            };
          } catch (orderError) {
            console.error("Error formatting order:", order.id, orderError);
            return null;
          }
        })
        .filter((order) => order !== null);

      console.log("Formatted orders:", formattedOrders);
      setOrders(formattedOrders);
      setError(null);

    } catch (err) {
      console.error("Fetch orders error:", err);

      let errorMessage = "Failed to fetch orders. Please try again.";

      if (err.response?.status === 401) {
        errorMessage = "Session expired. Please login again.";
        setOrders([]);
      } else if (err.response?.status === 404) {
        errorMessage = "No orders found for this user.";
        setOrders([]);
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please check your connection and try again.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh orders function that can be called externally
  const refreshOrders = useCallback(async () => {
    console.log("Refreshing orders...");
    await fetchOrders();
  }, [fetchOrders]);

  // Simple initial fetch on mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const placeOrder = async (orderData) => {
    // Prevent duplicate order placement
    if (orderPlacementInProgress.current) {
      console.log('Order placement already in progress, preventing duplicate');
      throw new Error('Order placement already in progress');
    }

    // Check for duplicate order using order identifier
    if (orderData.order_identifier && processedOrders.current.has(orderData.order_identifier)) {
      console.log('Duplicate order detected, preventing submission');
      throw new Error('Duplicate order detected');
    }

    if (!orderData.address_id) {
      throw new Error("Address not selected");
    }

    if (!orderData.shipping_method_id) {
      throw new Error("Shipping method not selected");
    }

    if (!orderData.payment_method) {
      throw new Error("Payment method missing");
    }

    // Mark order placement as in progress
    orderPlacementInProgress.current = true;

    // Mark this order as processed (but only after successful placement)
    // Don't mark as processed immediately to allow retry on failure
    let orderIdentifierToMark = null;
    if (orderData.order_identifier) {
      orderIdentifierToMark = orderData.order_identifier;
    }

    try {
      // Ensure promo discount is properly applied to the total before sending to API
      const promoDiscount = orderData.promo_discount || 0;

      // If total is already calculated (from BillingSection), use it
      // Otherwise, calculate it from products with promo discount applied
      if (!orderData.total && orderData.products) {
        const productsTotal = orderData.products.reduce((sum, product) => {
          return sum + (product.price * product.quantity);
        }, 0);

        const shippingCharges = orderData.shipping_charges || 0;
        const tax = orderData.tax || 0;

        // Apply promo discount to subtotal + shipping, then add tax
        const subtotalWithShipping = productsTotal + shippingCharges;
        const discountedSubtotal = Math.max(0, subtotalWithShipping - promoDiscount);
        orderData.total = discountedSubtotal + tax;
        orderData.subtotal = discountedSubtotal;

        console.log("Calculated order total with promo discount:", {
          productsTotal,
          shippingCharges,
          tax,
          promoDiscount,
          subtotalWithShipping,
          discountedSubtotal,
          finalTotal: orderData.total
        });
      }

      // Ensure the promo discount is properly reflected in the final order data
      const finalOrderData = {
        ...orderData,
        // Make sure promo_discount is included as promo_value for API compatibility
        promo_value: promoDiscount,
        // Ensure total reflects the discount
        total: orderData.total || 0,
        subtotal: orderData.subtotal || 0
      };

      // Log the order data to verify promo_code and discounted total
      console.log("Placing order with data:", finalOrderData);

      const response = await axios.post(`${BASE_URL}/place-orders`, finalOrderData);
      console.log("Order API response:", response.data);

      // Mark as processed only after successful placement
      if (orderIdentifierToMark) {
        processedOrders.current.add(orderIdentifierToMark);
      }

      // After successful order placement, refresh orders with a delay
      setTimeout(() => {
        refreshOrders();
      }, 1000);

      return response.data;
    } catch (error) {
      console.error("Place order error:", error.response?.data || error);

      // Don't mark as processed if placement failed - this allows retry
      // The order identifier was not added to processedOrders yet, so no need to remove

      throw error;
    } finally {
      // Reset order placement flag to allow retry
      orderPlacementInProgress.current = false;
    }
  };

  return (
    <OrderCancelProvider>
      <OrdersContext.Provider value={{
        orders,
        loading,
        error,
        placeOrder,
        refreshOrders
      }}>
        {children}
      </OrdersContext.Provider>
    </OrderCancelProvider>
  );
};
